import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import TaskBoardComponent from "./TaskBoardComponent";
import TaskDetails from "./TaskDetails";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";

export default function TaskBoardLists({ data, selectedMilestoneId }) {
  const dispatch = useDispatch();
  const cachedStatusData = useSelector((state) => state.trashandStatus.status);
  const [columns, setColumns] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [pendingTaskId, setPendingTaskId] = useState(null);

  const projectId = data?.project?._id || data?.project?.id;

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      if (!selectedMilestoneId || !projectId) return;
      setIsLoading(true);
      try {
        const [tasksRes, statusesRes] = await Promise.all([
          dispatch.projectDetails.getTasksByMilestone({
            project_id: projectId,
            milestone_id: selectedMilestoneId,
          }),
          cachedStatusData && cachedStatusData.length > 0
            ? null
            : dispatch.trashandStatus.getStatusData(),
        ]);

        const tasks = tasksRes?.data || [];
        const statuses =
          cachedStatusData && cachedStatusData.length > 0
            ? cachedStatusData
            : statusesRes?.data || [];

        const nextColumns = {};
        statuses.forEach((statusDoc) => {
          nextColumns[statusDoc._id] = {
            name: statusDoc.task_status,
            items: [],
          };
        });

        tasks.forEach((task) => {
          const statusId = task?.task_status?._id;
          if (statusId && nextColumns[statusId]) {
            nextColumns[statusId].items.push({
              id: task._id,
              content: task.task_name,
              priority: task.task_priority,
            });
          }
        });

        if (!isCancelled) {
          setColumns(nextColumns);
        }
      } catch (err) {
        if (!isCancelled) {
          toast.error(err?.message || "Failed to load the task board. Please try again.");
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [selectedMilestoneId, projectId, dispatch]);

  const onDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;

      // Dropped outside any column, or dropped back in the exact same spot.
      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const previousColumns = columns;
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];
      if (!sourceColumn || !destColumn) return;

      const sourceItems = [...sourceColumn.items];
      const [movedItem] = sourceItems.splice(source.index, 1);

      let nextColumns;
      if (source.droppableId === destination.droppableId) {
        sourceItems.splice(destination.index, 0, movedItem);
        nextColumns = {
          ...columns,
          [source.droppableId]: { ...sourceColumn, items: sourceItems },
        };
      } else {
        const destItems = [...destColumn.items];
        destItems.splice(destination.index, 0, movedItem);
        nextColumns = {
          ...columns,
          [source.droppableId]: { ...sourceColumn, items: sourceItems },
          [destination.droppableId]: { ...destColumn, items: destItems },
        };
      }

      // Optimistic update for instant, smooth feedback.
      setColumns(nextColumns);

      // Reordering within the same column isn't persisted (no ordering field on the backend).
      if (source.droppableId === destination.droppableId) return;

      setPendingTaskId(draggableId);
      try {
        await dispatch.projectDetails.projectTaskUpdataion({
          project_id: projectId,
          task_id: draggableId,
          task_status: destination.droppableId,
        });
      } catch (err) {
        // Roll back the optimistic move so the board matches the server state.
        setColumns(previousColumns);
        toast.error(
          err?.message || "Couldn't move the task. Please try again."
        );
      } finally {
        setPendingTaskId(null);
      }
    },
    [columns, dispatch, projectId]
  );

  if (isLoading) {
    return (
      <div className="max-w-full grid sm:grid-cols-2 xl:grid-cols-4 xl:w-full gap-6 overflow-x-auto pt-11 pb-7">
        <Skeleton count={4} />
        <Skeleton count={4} />
        <Skeleton count={4} />
        <Skeleton count={4} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex items-start gap-6 w-full overflow-x-auto pt-4 pb-4">
          {Object.entries(columns).map(([columnId, column]) => (
            <div key={columnId} className="w-[283px] shrink-0">
              <div className="flex items-center py-3 w-full px-4 rounded-t-theme-md bg-theme-cream-2">
                <div className="grow flex items-center gap-3">
                  <p className="text-theme-fg-2 font-semibold text-sm">
                    {column.name}
                  </p>
                  <label className="text-theme-fg-2 bg-theme-cream-3 px-2 py-1 rounded-theme-pill text-xs">
                    {column.items.length}
                  </label>
                </div>
              </div>

              <Droppable droppableId={columnId}>
                {(provided, snapshot) => (
                  <ul
                    className="w-full p-4 rounded-b-theme-md transition-colors duration-150 list-none"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      background: snapshot.isDraggingOver
                        ? "#E5E3DC"
                        : "#F7F6F3",
                      minHeight: 120,
                    }}
                  >
                    {column.items.length === 0 && !snapshot.isDraggingOver && (
                      <li className="text-xs text-theme-fg-3 text-center py-6 select-none">
                        No tasks
                      </li>
                    )}
                    {column.items.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                        isDragDisabled={pendingTaskId === item.id}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <li
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            style={{
                              userSelect: "none",
                              opacity: pendingTaskId === item.id ? 0.6 : 1,
                              ...dragProvided.draggableProps.style,
                            }}
                          >
                            <TaskBoardComponent
                              priority={item.priority}
                              taskName={item.content}
                              bgColor={
                                dragSnapshot.isDragging
                                  ? "bg-theme-paper shadow-theme-pop"
                                  : "bg-theme-paper"
                              }
                            />
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
      <TaskDetails />
    </div>
  );
}
